import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchDocumentPDF, useDocumentPDF } from '~/entities/document'
import {
  DOCUMENT_PDF_INTERACTION,
  useFirstScroll,
  useReadingDepth,
  useStepSession,
} from '~/features/document-analytics'
import { registerPdfTab } from '~/features/pdf-tab-reminder'
import { trackEvent } from '~/shared/lib/analytics'
import { cn } from '~/shared/lib/cn'
import { type PDFPaginationInfo, PDFDocument } from '~/shared/ui'

export function DocumentPDF({
  className,
  documentKey,
  initialFitToWidth = false,
}: {
  className?: string
  documentKey: string
  initialFitToWidth?: boolean
}) {
  const { step, documentKey: sessionDocKey, hasDownloaded, setHasDownloaded } = useStepSession()
  const { data: pdfUrl, isFetching } = useDocumentPDF(documentKey)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [isFetchingBlob, setIsFetchingBlob] = useState(false)
  const blobUrlRef = useRef<string | null>(null)

  // Analytics: track scroll engagement and reading depth
  const scrollableRef = useRef<HTMLElement | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)

  const handlePaginationReady = useCallback((info: PDFPaginationInfo) => {
    scrollableRef.current = info.scrollableRef.current
    setCurrentPage(info.currentPage)
    setTotalPages(info.totalPages)
  }, [])

  useFirstScroll(scrollableRef as React.RefObject<HTMLElement | null>)
  useReadingDepth(currentPage, totalPages)

  // Fetch and cache PDF blob when URL available
  useEffect(() => {
    if (!pdfUrl || blobUrl || isFetchingBlob) return

    const fetchBlob = async () => {
      setIsFetchingBlob(true)
      try {
        const response = await fetch(pdfUrl, { credentials: 'include' })
        if (response.status === 200) {
          const blob = await response.blob()
          const url = URL.createObjectURL(blob)
          blobUrlRef.current = url
          setBlobUrl(url)
        }
      } finally {
        setIsFetchingBlob(false)
      }
    }

    fetchBlob()
  }, [pdfUrl, blobUrl, isFetchingBlob])

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
      }
    }
  }, [])

  const handleInteraction = useCallback(
    (interactionType: string) => {
      trackEvent(DOCUMENT_PDF_INTERACTION, {
        step,
        document_key: sessionDocKey,
        interaction_type: interactionType,
      })
    },
    [step, sessionDocKey],
  )

  const handleDownload = async () => {
    trackEvent(DOCUMENT_PDF_INTERACTION, {
      step,
      document_key: sessionDocKey,
      interaction_type: 'download',
      is_first_download: !hasDownloaded,
    })
    setHasDownloaded()

    // Use cached blob URL if available
    if (blobUrl) {
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `${documentKey}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      return
    }

    // Fallback to original download logic
    const { data: pdfUrl } = await fetchDocumentPDF(documentKey)
    if (pdfUrl) {
      const response = await fetch(pdfUrl, { credentials: 'include' })
      const blob = await response.blob()
      const tempBlobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = tempBlobUrl
      link.download = `${documentKey}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(tempBlobUrl)
    }
  }

  // Use blob URL if available, otherwise fall back to presigned URL
  const fileUrl = blobUrl || pdfUrl || null
  const isLoading = isFetching || isFetchingBlob

  return (
    <div className="h-full">
      <PDFDocument
        className={cn(className)}
        file={fileUrl}
        isLoading={isLoading}
        onDownload={handleDownload}
        onOpenInNewTab={() => {
          const win = window.open(`/documents/${documentKey}/view-pdf`, '_blank')
          registerPdfTab(documentKey, win)
        }}
        onInteraction={handleInteraction}
        initialFitToWidth={initialFitToWidth}
        onPaginationReady={handlePaginationReady}
      />
    </div>
  )
}
